import type {
  AttendanceCustomPeriodEmployeeItemFragment,
  AttendanceEmployeesSettingExtraHoursBaselineEnum,
} from 'generated/resources/graphql'
import { groupBy } from 'lodash'

import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'

import ExtraHoursUtils from 'modules/attendance/lib/utils/ExtraHoursUtils'

/**
 * Captures the operands used to derive extra time so the UI can explain the breakdown.
 */
type ExtraTimeInfo = {
  minutes: number | undefined
  baseline: AttendanceEmployeesSettingExtraHoursBaselineEnum | undefined
  baselineMinutes: number
  comparedMinutes: number
}

type BaselineInfo = {
  type: AttendanceEmployeesSettingExtraHoursBaselineEnum | undefined
  baselineMinutes: number
  comparedMinutes: number
}

type AggregateMinutesResult = {
  balanceMinutes: number
  plannedMinutes: number
  contractMinutes: number
  workedMinutes: number
  regularMinutes: number
  paidBreaksMinutes: number
  paidAbsencesMinutes: number
  dailyDays: number
  effectiveWorkedMinutes: number
  workedExtraMinutes: number
  baseline: BaselineInfo
  extraTime: ExtraTimeInfo
  extraHoursCalculationOn: string | undefined
  balancedRegularMinutes: number
}

const getRelevantBalance = (employee: AttendanceCustomPeriodEmployeeItemFragment, today: Date) => {
  const todayString = i18nDateToString(today, 'backend')
  const groupedBalances = groupBy(employee.attendanceBalances, 'date')
  const todayBalance = groupedBalances[todayString]?.[0]
  const lastBalance = employee.attendanceBalances[employee.attendanceBalances.length - 1]

  const isLastBalanceFromTodayOrBefore = stringToDate(
    lastBalance?.date ?? '',
    'backend'
  )?.isSameOrBefore(today)

  return isLastBalanceFromTodayOrBefore ? lastBalance : todayBalance
}

const getExtraHoursCalculationOn = (
  relevantBalance: { extraTimeCalculationOn?: string } | undefined
) => {
  const extraHoursCalculationOn = stringToDate(
    relevantBalance?.extraTimeCalculationOn ?? '',
    'backend'
  )

  return extraHoursCalculationOn
    ? i18nDateToString(extraHoursCalculationOn, 'ui_dmy_long')
    : undefined
}

const sumDailyBalanceUntil = (employee: AttendanceCustomPeriodEmployeeItemFragment) =>
  // Backend already trims balances to the requested period, so we simply add everything the
  // payload contains instead of enforcing an extra frontend cutoff.
  employee.attendanceBalances?.reduce(
    (acc, node) => acc + Number(node.dailyBalanceFromPlanning),
    0
  ) ?? 0

const sumPlannedMinutesUntil = (employee: AttendanceCustomPeriodEmployeeItemFragment) =>
  // Planned minutes arrive constrained by the server-selected window (start/endOn), which avoids
  // double counting future estimates on the client.
  employee.attendanceEstimatedTimes?.reduce((acc, node) => acc + Number(node.minutes ?? 0), 0) ?? 0

const sumContractMinutes = (employee: AttendanceCustomPeriodEmployeeItemFragment) =>
  employee.attendanceEstimatedTimes?.reduce(
    (acc, node) => acc + Number(node.contractMinutes ?? 0),
    0
  ) ?? 0

const calculateBaselineInfo = ({
  baseline,
  plannedMinutes,
  contractMinutes,
  effectiveWorkedMinutes,
  workedMinutes,
}: {
  baseline: AttendanceEmployeesSettingExtraHoursBaselineEnum | undefined
  plannedMinutes: number
  contractMinutes: number
  effectiveWorkedMinutes: number
  workedMinutes: number
}): BaselineInfo => {
  // Without an explicit policy baseline we fall back to the backend balance pairing – planned
  // vs effective worked time – so the UI continues to present a meaningful delta.
  if (!baseline) {
    return {
      type: undefined,
      baselineMinutes: plannedMinutes,
      comparedMinutes: effectiveWorkedMinutes,
    }
  }

  const isContractBaseline = baseline === 'contract_hours'

  return {
    type: baseline,
    baselineMinutes: isContractBaseline ? contractMinutes : plannedMinutes,
    comparedMinutes: isContractBaseline ? effectiveWorkedMinutes : workedMinutes,
  }
}

/**
 * Normalises the backend-provided attendance totals into the structure used by the
 * time-tracking UI, preserving backend calculations (regular/effective minutes, balance,
 * extra time) so the breakdown always matches server figures.
 */
export const aggregateMinutes = (
  employee: AttendanceCustomPeriodEmployeeItemFragment,
  baselineOverride?: AttendanceEmployeesSettingExtraHoursBaselineEnum
): AggregateMinutesResult => {
  const now = new Date()
  const relevantBalance = getRelevantBalance(employee, now)

  const workedMinutes = employee.attendanceAggregatedWorkedTime?.minutes ?? 0
  const paidAbsencesMinutes = employee.attendanceAggregatedWorkedTime?.paidAbsencesMinutes ?? 0
  const paidBreaksMinutes = employee.attendanceAggregatedWorkedTime?.paidBreaksMinutes ?? 0
  const effectiveWorkedMinutes =
    employee.attendanceAggregatedWorkedTime?.effectiveWorkedMinutes ??
    workedMinutes + paidAbsencesMinutes
  const dailyDays = employee.attendanceAggregatedWorkedTime?.dailyDays ?? 0

  const regularMinutes = employee.attendanceAggregatedWorkedTime?.regularMinutes ?? 0
  const plannedMinutes = sumPlannedMinutesUntil(employee)
  const contractMinutes = sumContractMinutes(employee)
  const balanceMinutes = sumDailyBalanceUntil(employee)

  const baseline =
    baselineOverride ??
    employee.attendanceEmployeesPolicy?.attendanceEmployeesSettingsConnection?.edges?.[0]?.node
      ?.extraHoursBaseline ??
    undefined
  const baselineInfo = calculateBaselineInfo({
    baseline,
    plannedMinutes,
    contractMinutes,
    effectiveWorkedMinutes,
    workedMinutes,
  })
  // Worked extra minutes come directly from the backend aggregation (extra time blocks).
  const workedExtraMinutes = employee.attendanceAggregatedWorkedTime?.extraTimeMinutes ?? 0
  const hasExtraTimeCalculation = Boolean(relevantBalance?.extraTimeCalculated)

  const balances = employee.attendanceBalances ?? []

  const aggregatedExtraHoursMinutes = ExtraHoursUtils.sumExtraHoursWithinRange({
    balances,
  })

  // Only surface extra time when the backend flags that the calculation is ready; otherwise
  // we keep it undefined to avoid drifting from backend totals.
  const extraTimeMinutes =
    baselineInfo.type && hasExtraTimeCalculation ? aggregatedExtraHoursMinutes : undefined

  const extraHoursCalculationOn = getExtraHoursCalculationOn(relevantBalance)

  const balancedRegularMinutes = regularMinutes + workedExtraMinutes - (extraTimeMinutes ?? 0)

  return {
    balanceMinutes,
    plannedMinutes,
    contractMinutes,
    workedMinutes,
    regularMinutes,
    paidBreaksMinutes,
    paidAbsencesMinutes,
    dailyDays,
    effectiveWorkedMinutes,
    workedExtraMinutes,
    baseline: baselineInfo,
    extraTime: {
      minutes: extraTimeMinutes,
      baseline: baselineInfo.type,
      baselineMinutes: baselineInfo.baselineMinutes,
      comparedMinutes: baselineInfo.comparedMinutes,
    },
    extraHoursCalculationOn,
    balancedRegularMinutes,
  }
}

export type { AggregateMinutesResult }
